import React, { useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { formatCategory, isUnclassifiedCategory } from "./shared";
import ThemedChartTooltip, { ThemedTooltipBox } from "../shared/ThemedChartTooltip";
import { SkeletonChart } from "../shared/Skeleton";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";

const CATEGORY_PALETTE = [
  "#32B1FF", // blue
  "#10B981", // emerald
  "#8B5CF6", // purple
  "#EAB308", // amber
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#06B6D4", // cyan
  "#A855F7", // violet
];

const UNCLASSIFIED_COLOR = "#9ca3af";
const UNCLASSIFIED_PATTERN_ID = "ei-unclassified-stripes";

// Coverage alert thresholds — flag when the classifier is missing a meaningful share.
// A healthy run should sit well under 30%; 50%+ is a clear quality crisis.
const UNCLASSIFIED_WARN_PCT = 30;
const UNCLASSIFIED_CRITICAL_PCT = 50;

const isUnclassified = (category) => {
  // Also catch "uncategorized" from legacy payloads; the shared helper handles null/""/"Undefined"/"undefined".
  if (typeof category === "string" && /^uncategorized$/i.test(category.trim())) return true;
  return isUnclassifiedCategory(category);
};

const TooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload;
  if (!datum) return null;
  return (
    <ThemedTooltipBox>
      <div className="flex items-center gap-2 mb-0.5">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: datum.__color }} />
        <span style={{ fontWeight: 600 }}>{formatCategory(datum.category)}</span>
      </div>
      <div style={{ opacity: 0.75 }}>
        {datum.count} event{datum.count === 1 ? "" : "s"} · {datum.pct}%
      </div>
    </ThemedTooltipBox>
  );
};

const CategoryDistributionPanel = ({ loading, error, rows, rangeControl = null }) => {
  const data = useMemo(() => {
    const categoryRows = Array.isArray(rows) ? rows : [];
    const classified = [];
    let unclassifiedCount = 0;
    let unclassifiedPct = 0;
    let hasUnclassified = false;

    for (const row of categoryRows) {
      const count = Number(row.count || 0);
      const pct = Number(row.pct || 0);
      if (isUnclassified(row.category)) {
        hasUnclassified = true;
        unclassifiedCount += count;
        unclassifiedPct += pct;
      } else {
        classified.push({ ...row, count, pct });
      }
    }

    let paletteIdx = 0;
    const result = classified.map((row) => ({
      ...row,
      __color: CATEGORY_PALETTE[paletteIdx++ % CATEGORY_PALETTE.length],
    }));

    if (hasUnclassified) {
      result.push({
        category: null,
        count: unclassifiedCount,
        pct: Math.round(unclassifiedPct * 10) / 10,
        __color: UNCLASSIFIED_COLOR,
      });
    }

    return result;
  }, [rows]);

  const total = useMemo(() => data.reduce((sum, datum) => sum + datum.count, 0), [data]);

  const unclassified = useMemo(() => {
    const row = data.find((datum) => isUnclassified(datum.category));
    if (!row || total === 0) return null;
    const pct = Math.round((row.count / total) * 1000) / 10;
    let level = null;
    if (pct >= UNCLASSIFIED_CRITICAL_PCT) level = "critical";
    else if (pct >= UNCLASSIFIED_WARN_PCT) level = "warn";
    return { count: row.count, pct, level };
  }, [data, total]);

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-4">
        <div>
          <div className="flex items-center">
            <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Category Distribution</h3>
            <InfoTip text="Pair with the Category Affinity Heatmap to spot mismatches — categories that dominate your local supply but have low affinity with your accommodation types are weak demand." />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Share of discovered events by category.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap md:flex-nowrap md:shrink-0">
          {unclassified?.level && !loading && (
            <span
              title={`${unclassified.count} of ${total} events are unclassified — classifier coverage is ${unclassified.level === "critical" ? "low" : "degraded"}.`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium max-[499px]:hidden ${
                unclassified.level === "critical"
                  ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
                  : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${unclassified.level === "critical" ? "bg-red-500" : "bg-amber-500"}`} />
              {unclassified.pct}% unclassified
            </span>
          )}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {loading ? "Loading…" : `${total} event${total === 1 ? "" : "s"}`}
          </div>
          {rangeControl}
        </div>
      </div>

      {unclassified?.level && !loading && (
        <div
          title={`${unclassified.count} of ${total} events are unclassified — classifier coverage is ${unclassified.level === "critical" ? "low" : "degraded"}.`}
          className={`hidden max-[499px]:flex w-full items-center justify-center gap-1 rounded-full px-2 py-1 text-[11px] font-medium mb-4 ${
            unclassified.level === "critical"
              ? "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
              : "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${unclassified.level === "critical" ? "bg-red-500" : "bg-amber-500"}`} />
          {unclassified.pct}% unclassified
        </div>
      )}

      {loading ? (
        <SkeletonChart height={288} />
      ) : error ? (
        <ErrorState message={error} />
      ) : data.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          No category data available yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
          <div className="relative h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  <pattern id={UNCLASSIFIED_PATTERN_ID} patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill={UNCLASSIFIED_COLOR} />
                    <line x1="0" y1="0" x2="0" y2="6" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
                  </pattern>
                </defs>
                <Pie
                  data={data}
                  dataKey="count"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  innerRadius={70}
                  outerRadius={110}
                  paddingAngle={2}
                >
                  {data.map((datum, index) => (
                    <Cell
                      key={index}
                      fill={isUnclassified(datum.category) ? `url(#${UNCLASSIFIED_PATTERN_ID})` : datum.__color}
                      stroke="none"
                    />
                  ))}
                </Pie>
                <ThemedChartTooltip content={<TooltipContent />} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-3xl font-bold text-gray-900 dark:text-gray-100">{total}</div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 dark:text-gray-500">
                total events
              </div>
            </div>
          </div>

          <div className="space-y-1.5">
            {data.map((datum, index) => (
              <div
                key={index}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg"
              >
                <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: datum.__color }} />
                <span className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-100 truncate">
                  {formatCategory(datum.category)}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                  {datum.count}
                </span>
                <span className="text-xs font-semibold text-gray-900 dark:text-gray-100 w-10 text-right shrink-0">
                  {datum.pct}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryDistributionPanel;
